import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { GridApi, IRowNode } from 'ag-grid-enterprise'
import { EnhancedStatementRow } from '../utils/dataEnhancement'
import { fetchExpandedDimensionRows } from './useDimensionLoading'
import { Dimension } from '../types'

/**
 * Manages tree data updates using AG Grid transactions instead of rowData prop.
 * This preserves expanded state and async-loaded child rows when the main data updates.
 *
 * How it works:
 * 1. Tracks previous data by row key
 * 2. When new data arrives, calculates diff (added, updated, removed)
 * 3. Fetches dimension data for expanded nodes when parent updates
 * 4. Applies both parent and dimension updates in single transaction
 */
export const useTreeDataTransaction = (
  gridApi: GridApi<EnhancedStatementRow> | undefined,
  treeData: EnhancedStatementRow[],
  isLoading: boolean,
  companyId?: string,
  budgetingScenarioId?: number | null,
  dimensions?: string,
  getDimensionParams?: (row: EnhancedStatementRow) => any,
  dimensionMap?: Map<string | null, Dimension[]>,
  dimensionList?: Dimension[]
) => {
  const previousDataRef = useRef<Map<string, EnhancedStatementRow>>(new Map())
  const isInitializedRef = useRef(false)
  const isTransactionInProgressRef = useRef(false)
  const [isDimensionLoading, setIsDimensionLoading] = useState(false)
  const previousLoadingRef = useRef(isLoading)
  const isDimensionRefetchInProgressRef = useRef(false)

  // Reset state when key parameters change (e.g., company, scenario, or dimension filter switch, or hot reload)
  useEffect(() => {
    // IMPORTANT: Always clear grid data when reset happens, regardless of isInitialized
    // This prevents duplicates when switching tabs/reports
    if (gridApi) {
      gridApi.setGridOption('rowData', [])
    }

    isInitializedRef.current = false
    previousDataRef.current.clear()
  }, [companyId, budgetingScenarioId, dimensions, gridApi])

  // Refetch dimension rows when data refetch completes
  useEffect(() => {
    const wasLoading = previousLoadingRef.current
    const isNowLoaded = !isLoading
    previousLoadingRef.current = isLoading

    // When loading completes (was loading, now not loading), refetch all expanded dimension rows
    if (
      wasLoading &&
      isNowLoaded &&
      gridApi &&
      isInitializedRef.current &&
      getDimensionParams &&
      dimensionMap &&
      dimensionList &&
      !isDimensionRefetchInProgressRef.current
    ) {
      // Mark refetch in progress
      isDimensionRefetchInProgressRef.current = true

      // Force synchronous state update to ensure spinner shows immediately
      flushSync(() => {
        setIsDimensionLoading(true)
      })

      // Refetch dimension data
      const refetchDimensions = async () => {
        try {
          const dimensionRows = await fetchExpandedDimensionRows(
            gridApi,
            undefined,
            getDimensionParams,
            dimensionMap,
            dimensionList
          )

          if (dimensionRows.length > 0) {
            gridApi.applyTransaction({ update: dimensionRows })
            gridApi.refreshCells({ force: true })
          }
        } catch (error) {
          console.error('❌ Error refetching dimension rows:', error)
        } finally {
          isDimensionRefetchInProgressRef.current = false
          setIsDimensionLoading(false)
        }
      }

      refetchDimensions()
    }
  }, [isLoading, gridApi, getDimensionParams, dimensionMap, dimensionList])

  useEffect(() => {
    if (!gridApi || isLoading) return

    // Skip if a transaction is already in progress
    if (isTransactionInProgressRef.current) {
      return
    }

    const currentDataMap = new Map<string, EnhancedStatementRow>()

    // Build map of current data by key
    const buildDataMap = (rows: EnhancedStatementRow[]) => {
      rows.forEach(row => {
        currentDataMap.set(row.key, row)
      })
    }

    buildDataMap(treeData)

    // Initial load - apply directly (no dimension updates needed as nothing is expanded yet)
    if (!isInitializedRef.current) {
      const currentRowCount = gridApi.getDisplayedRowCount()

      // IMPORTANT: If grid already has rows (e.g., after hot reload), clear it first
      // This prevents duplicate row errors during development hot reload
      if (currentRowCount > 0) {
        gridApi.setGridOption('rowData', [])
      }

      gridApi.applyTransaction({ add: treeData })
      previousDataRef.current = currentDataMap
      isInitializedRef.current = true
      return
    }

    // Calculate diff between previous and current data
    const toAdd: EnhancedStatementRow[] = []
    const toUpdate: EnhancedStatementRow[] = []
    const toRemove: EnhancedStatementRow[] = []

    // Build a Set of all existing keys in the grid (O(n) instead of O(n×m))
    // This includes both visible and filtered rows
    const existingKeys = new Set<string>()
    gridApi.forEachNode(node => {
      if (node.data && node.data.key) {
        existingKeys.add(node.data.key)
      }
    })

    // Find added and updated rows
    currentDataMap.forEach((currentRow, key) => {
      const previousRow = previousDataRef.current.get(key)
      const existsInGrid = existingKeys.has(key)

      if (!previousRow) {
        // New row - add it
        toAdd.push(currentRow)
      } else if (existsInGrid) {
        // Row exists in grid (visible or filtered) - update it
        toUpdate.push(currentRow)
      }
      // If row exists in memory but not in grid, don't add it back
      // This prevents duplicate node errors when rows are filtered
    })

    // Find removed rows
    previousDataRef.current.forEach((previousRow, key) => {
      if (!currentDataMap.has(key)) {
        // Only remove if it's a main report row (not async-loaded)
        // Async-loaded rows (dimensions, investments, amendments) don't exist in main API response
        const isAsyncLoadedRow =
          previousRow.rowType === 'DIMENSION' ||
          previousRow.rowType === 'INVESTMENT' ||
          previousRow.rowType === 'AMENDMENT'

        if (!isAsyncLoadedRow) {
          toRemove.push(previousRow)
        }
      }
    })

    // Apply transaction if there are changes
    if (toAdd.length > 0 || toUpdate.length > 0 || toRemove.length > 0) {
      // Save old data before updating ref (needed for budgeting method comparison in async transaction)
      const oldDataMap = previousDataRef.current

      // IMPORTANT: Update previousDataRef IMMEDIATELY to prevent duplicate triggers
      // while async dimension fetch is in progress
      previousDataRef.current = currentDataMap

      // Set loading state ONLY if dimension refetch is NOT in progress
      // (dimension refetch manages its own loading state)
      if (!isDimensionRefetchInProgressRef.current) {
        setIsDimensionLoading(true)
      }

      // Apply transaction with dimension updates
      // IMPORTANT: This is async - we wait for dimension data before applying
      const applyTransactionAsync = async () => {
        // Mark transaction as in progress
        isTransactionInProgressRef.current = true

        try {
          // Check for budgeting method changes and handle affected rows
          const dimensionRowsToRemove: EnhancedStatementRow[] = []
          const parentNodesToCollapse: IRowNode<EnhancedStatementRow>[] = []
          const accountsWithMethodChanges = new Set<number>()

          // First pass: identify which accounts have budgeting method changes
          toUpdate.forEach(row => {
            const prevRow = oldDataMap.get(row.key)
            if (
              prevRow &&
              row._sourceAccountCode &&
              JSON.stringify(prevRow.budgetingMethod) !== JSON.stringify(row.budgetingMethod)
            ) {
              accountsWithMethodChanges.add(row._sourceAccountCode)
            }
          })

          // Second pass: if any accounts have changes, find and mark nodes for collapse/removal
          if (accountsWithMethodChanges.size > 0) {
            gridApi.forEachNode(node => {
              if (!node.data) return

              const sourceAccountCode = node.data._sourceAccountCode

              // Check if this node belongs to an account with method changes
              if (sourceAccountCode && accountsWithMethodChanges.has(sourceAccountCode)) {
                // Mark parent nodes (account rows) for collapse
                if (node.data.key === `ACCOUNT-${node.data._sourceAccountCode}`) {
                  parentNodesToCollapse.push(node)
                }

                // Mark dimension rows for removal (but keep DIMENSION_GROUP placeholder)
                if (node.data.rowType === 'DIMENSION') {
                  dimensionRowsToRemove.push(node.data)
                }
              }
            })

            // Collapse parent nodes before removing dimension rows
            parentNodesToCollapse.forEach(node => {
              gridApi.setRowNodeExpanded(node, false)
            })
          }

          // Now apply combined transaction atomically
          const combinedTransaction = {
            add: toAdd.length > 0 ? toAdd : undefined,
            update: toUpdate.length > 0 ? toUpdate : undefined,
            remove:
              toRemove.length > 0 || dimensionRowsToRemove.length > 0
                ? [...toRemove, ...dimensionRowsToRemove]
                : undefined
          }

          // Apply combined transaction
          const result = gridApi.applyTransaction(combinedTransaction)

          // Force cell refresh to update icons/displays
          if (result?.update && result.update.length > 0) {
            gridApi.refreshCells({
              rowNodes: result.update,
              force: true
            })
            // Reset row heights in case budgeting method changed (affects dual-value display)
            gridApi.resetRowHeights()
          }
        } catch (error) {
          console.error('❌ Error applying transaction:', error)
        } finally {
          // Mark transaction as complete
          isTransactionInProgressRef.current = false
          // Only clear loading if dimension refetch is NOT in progress
          if (!isDimensionRefetchInProgressRef.current) {
            setIsDimensionLoading(false)
          }
        }
      }

      // Execute async transaction
      applyTransactionAsync()
    } else {
      // No changes - just update reference
      previousDataRef.current = currentDataMap
    }
  }, [gridApi, treeData, isLoading, dimensions, getDimensionParams, dimensionMap, dimensionList])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isInitializedRef.current = false
      previousDataRef.current.clear()
    }
  }, [])

  return { isDimensionLoading }
}
